import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Loader2, Save, X, UserCog, Briefcase, Trash2 } from "lucide-react";
import { usersService, User } from "@/services/usersService";
import { getCompanies, type Company } from "@/services/companiesService";
import { getBranches, type Branch } from "@/services/branchesService";
import { verifyCredentials, getCurrentUser } from "@/services/authService";
import DeleteWithAuthDialog from "@/components/DeleteWithAuthDialog";

// role_id: 2 = dueño, 3 = gerente
interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;       // null = crear, User = editar
  roleId: 2 | 3;
  onSave?: () => void;
}

interface FormData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_id: string;
  branch_id: string;
  is_active: boolean;
}

const initialFormData: FormData = {
  username: "",
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  company_id: "",
  branch_id: "",
  is_active: true,
};

const ROLE_LABELS: Record<number, string> = {
  2: "Dueño",
  3: "Gerente",
};

export default function UserFormModal({
  isOpen,
  onClose,
  user,
  roleId,
  onSave,
}: UserFormModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const isEditing = !!user;
  const isManager = roleId === 3;
  const roleLabel = ROLE_LABELS[roleId];

  // Cargar datos cuando abre el modal
  useEffect(() => {
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);

  // Rellenar formulario al editar
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email || "",
        password: "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        company_id: user.company_id || "",
        branch_id: user.branch_id || "",
        is_active: user.is_active,
      });
      // Si tiene empresa, cargar sus sucursales
      if (user.company_id) {
        loadBranches(user.company_id);
      }
    } else {
      setFormData(initialFormData);
      setBranches([]);
    }
  }, [user, isOpen]);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch {
      toast.error("Error al cargar empresas");
    }
  };

  const loadBranches = async (companyId: string) => {
    if (!companyId) {
      setBranches([]);
      return;
    }
    setLoadingBranches(true);
    try {
      const data = await getBranches(companyId);
      setBranches(data);
    } catch {
      toast.error("Error al cargar sucursales");
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Si cambia la empresa, resetear la sucursal y recargar opciones
      if (field === "company_id") {
        next.branch_id = "";
        loadBranches(value as string);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.username) {
      toast.error("Nombre, apellido y usuario son obligatorios");
      return;
    }
    if (!isEditing && !formData.password) {
      toast.error("La contraseña es obligatoria al crear un usuario");
      return;
    }
    if (!formData.company_id) {
      toast.error("La empresa es obligatoria");
      return;
    }
    if (isManager && !formData.branch_id) {
      toast.error("La sucursal es obligatoria para un gerente");
      return;
    }

    setIsLoading(true);
    try {
      if (isEditing && user) {
        const updateData: any = {
          email: formData.email || null,
          first_name: formData.first_name,
          last_name: formData.last_name,
          company_id: formData.company_id || null,
          branch_id: isManager ? (formData.branch_id || null) : null,
          is_active: formData.is_active,
        };
        if (formData.password) updateData.password = formData.password;

        await usersService.updateUser(user.id, updateData);
        toast.success(`${roleLabel} actualizado correctamente`);
      } else {
        await usersService.createUser({
          username: formData.username,
          password: formData.password,
          email: formData.email || undefined,
          role_id: roleId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          company_id: formData.company_id || undefined,
          branch_id: isManager ? (formData.branch_id || undefined) : undefined,
          is_active: formData.is_active,
        });
        toast.success(`${roleLabel} creado correctamente`);
      }

      onSave?.();
      onClose();
    } catch (error: any) {
      toast.error(error.message || `Error al guardar el ${roleLabel.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setBranches([]);
    onClose();
  };

  const handleDeleteConfirm = async (password: string) => {
    if (!user) return;
    const currentUser = getCurrentUser();
    if (!currentUser) { toast.error("Sesión no encontrada"); return; }

    setIsDeleting(true);
    try {
      const valid = await verifyCredentials(currentUser.username, password);
      if (!valid) {
        toast.error("Contraseña incorrecta");
        return;
      }
      await usersService.deleteUser(user.id);
      toast.success(`${roleLabel} eliminado correctamente`);
      setIsDeleteOpen(false);
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Error al eliminar el ${roleLabel.toLowerCase()}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isManager ? <Briefcase className="h-5 w-5" /> : <UserCog className="h-5 w-5" />}
            {isEditing ? `Editar ${roleLabel}` : `Nuevo ${roleLabel}`}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {isEditing
              ? `Modifica los datos del ${roleLabel.toLowerCase()}.`
              : `Completa los campos para registrar un nuevo ${roleLabel.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4 py-4">

            {/* Nombre y Apellido */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Nombre *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Juan"
                  className="bg-slate-800 border-slate-600"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Apellido *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Pérez"
                  className="bg-slate-800 border-slate-600"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Usuario */}
            <div className="space-y-2">
              <Label htmlFor="username">Usuario *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleChange("username", e.target.value)}
                placeholder="juanperez"
                className="bg-slate-800 border-slate-600"
                disabled={isEditing || isLoading}
              />
              {isEditing && (
                <p className="text-xs text-white/40">El nombre de usuario no se puede cambiar</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="juan@ejemplo.com"
                className="bg-slate-800 border-slate-600"
                disabled={isLoading}
              />
            </div>

            {/* Contraseña */}
            <div className="space-y-2">
              <Label htmlFor="password">
                {isEditing ? "Nueva Contraseña (vacío = sin cambio)" : "Contraseña *"}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder="••••••••"
                className="bg-slate-800 border-slate-600"
                disabled={isLoading}
              />
            </div>

            {/* Empresa */}
            <div className="space-y-2">
              <Label htmlFor="company_id">Empresa *</Label>
              <select
                id="company_id"
                value={formData.company_id}
                onChange={(e) => handleChange("company_id", e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isLoading}
              >
                <option value="">Selecciona una empresa</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {companies.length === 0 && (
                <p className="text-xs text-amber-400/70">No hay empresas. Crea una primero.</p>
              )}
            </div>

            {/* Sucursal (solo para gerentes) */}
            {isManager && (
              <div className="space-y-2">
                <Label htmlFor="branch_id">Sucursal *</Label>
                <select
                  id="branch_id"
                  value={formData.branch_id}
                  onChange={(e) => handleChange("branch_id", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  disabled={isLoading || loadingBranches || !formData.company_id}
                >
                  <option value="">
                    {!formData.company_id
                      ? "Selecciona primero una empresa"
                      : loadingBranches
                      ? "Cargando sucursales..."
                      : "Selecciona una sucursal"}
                  </option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {formData.company_id && branches.length === 0 && !loadingBranches && (
                  <p className="text-xs text-amber-400/70">
                    No hay sucursales para esta empresa. Crea una primero.
                  </p>
                )}
              </div>
            )}

            {/* Estado activo */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <Label htmlFor="is_active">Usuario Activo</Label>
                <p className="text-xs text-white/40 mt-0.5">
                  Los usuarios inactivos no pueden iniciar sesión
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => handleChange("is_active", checked)}
                disabled={isLoading}
              />
            </div>

            {/* Zona de peligro — solo en edición */}
            {isEditing && (
              <>
                <Separator className="bg-white/10 my-2" />
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-red-400/70">
                    Zona de peligro
                  </p>
                  <p className="text-xs text-white/40">
                    Eliminar al {roleLabel.toLowerCase()} de forma permanente. Esta acción
                    no se puede deshacer. Si solo quieres bloquear el acceso, usa el
                    interruptor &ldquo;Usuario Activo&rdquo; de arriba.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDeleteOpen(true)}
                    disabled={isLoading || isDeleting}
                    className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar {roleLabel.toLowerCase()} permanentemente
                  </Button>
                </div>
              </>
            )}

          </div>
        </ScrollArea>

        <DialogFooter className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditing ? "Actualizar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Diálogo de confirmación + reautenticación para eliminar */}
    {isEditing && user && (
      <DeleteWithAuthDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        resourceType={roleLabel}
        resourceName={usersService.getFullName(user)}
        cascadeItems={[]}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    )}
  </>
  );
}
